/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type KeyboardEvent, useCallback, useEffect, useMemo, useRef, useTransition } from 'react';
import {
    type ShouldRevalidateFunctionArgs,
    useAsyncError,
    useLocation,
    useNavigation,
    useSearchParams,
} from 'react-router';
import type { Route } from './+types/_app.search';
import type { ShopperSearch } from '@/scapi';
import { NormalizedApiError } from '@/lib/api/normalized-api-error';
import { fetchSearchProducts } from '@/lib/api/search.server';
import { fetchWishlistInitialState } from '@/lib/wishlist/fetch-initial-state.server';
import type { WishlistInitialState } from '@/lib/wishlist/state';
import { WishlistProvider } from '@/providers/wishlist';
import { getConfig, useConfig } from '@salesforce/storefront-next-runtime/config';
import { siteContext } from '@salesforce/storefront-next-runtime/site-context';
import { getLogger } from '@/lib/logger.server';
import CategoryPagination from '@/components/category-pagination';
import ActiveFilters from '@/components/category-refinements/active-filters';
import FiltersButton from '@/components/category-refinements/filters-button';
import CategoryRefinements from '@/components/category-refinements';
import CategorySorting from '@/components/category-sorting';
import DeferredProductGrid from '@/components/product-grid';
import { firstTileImageUrl } from '@/components/product-grid/to-product-tile-card-props';
import ArticleTile from '@/components/article-tile';
import { getPublicOrigin } from '@/utils/schema-url';
import { resolvePageMetaDescription } from '@/lib/seo/description';
import { resolveOgImage } from '@/lib/seo/og-image';
import { searchLearnArticles } from '@/lib/learn/search-articles.server';
import type { LearnArticle } from '@/lib/learn/catalog.server';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@/hooks/use-analytics';
import { PageType } from '@/lib/decorators/page-type';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { type DefaultSettingsValue, type DefaultSettingsTheme } from '@/lib/page-designer/default-settings';
import { resolveLastComponentTheme } from '@/lib/page-designer/last-component-theme';
import { cn } from '@/lib/utils';
import { Region } from '@/components/region';
import { SeoMeta } from '@/components/seo-meta';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { buildPageName, pushDataLayer, type RouteAnalytics } from '@/analytics/data-layer';
import { fetchPageWithComponentData } from '@/lib/page-designer/page-loader.server';
import {
    getInitialFiltersOpen,
    getSearchWithoutClientOnlyParams,
    getSearchWithoutFiltersParam,
    useFiltersPanelState,
} from '@/hooks/use-filters-panel-state';

@PageType({
    name: 'Search Results Page',
    description: 'Search results page with product listings and personalized content',
    supportedAspectTypes: [],
})
@RegionDefinition([
    {
        id: 'searchTopFullWidth',
        name: 'Top Full Width Region',
        description: 'Full screen width region at the top of search results',
        maxComponents: 5,
    },
    {
        id: 'searchTopContent',
        name: 'Top Content Region',
        description: 'Full-width region below the results header, above the product grid',
        maxComponents: 5,
    },
    {
        id: 'searchBottom',
        name: 'Bottom Region',
        description: 'Region at the bottom of search results after product grid',
        maxComponents: 5,
    },
])
export class SearchPageMetadata {
    /* v8 ignore start - decorators are exercised by the generate-cartridge step, not unit tests */
    @AttributeDefinition({
        id: 'settings',
        name: 'Default Settings',
        description:
            'Theme applied to the search-results grid section background (light/dark). Defaults to light. Dark uses the same background as Page Designer components on their dark theme.',
        type: 'custom',
        editorDefinition: { type: 'inline.defaultSettings' },
    })
    settings?: DefaultSettingsValue;
    /* v8 ignore stop */
}

/**
 * Resolve the search grid section theme from the page settings. Defaults to **light** (the current
 * search appearance); a merchandiser opts into the dark grid via Default Settings. Both themes use
 * the shared `bm-theme-light` / `bm-theme-dark` classes, so the dark grid background (`--bm-ink`)
 * matches the dark theme of Page Designer components.
 */
function resolveSearchTheme(settings?: DefaultSettingsValue): DefaultSettingsTheme {
    return settings?.themeColor === 'dark' ? 'dark' : 'light';
}

/** The two search result tabs (UCR-2778). Tab state lives in the `type` URL param. */
export type SearchTab = 'products' | 'content';
const SEARCH_TABS: readonly SearchTab[] = ['products', 'content'];
const TAB_PARAM = 'type';

/** Active tab = the `type` search param when valid, else the loader's computed default. */
export function resolveActiveTab(param: string | null, fallback: SearchTab): SearchTab {
    return SEARCH_TABS.includes(param as SearchTab) ? (param as SearchTab) : fallback;
}

export type SearchPageData = {
    searchTerm: string;
    searchResultCritical: ShopperSearch.schemas['ProductSearchResult'];
    searchResultNonCritical: Promise<ShopperSearch.schemas['ProductSearchResult']>;
    // The loader awaits the page (so the section theme is ready at first paint), but `<Region>` also
    // accepts the unresolved promise — allow both so test fixtures can pass a resolved promise.
    page: Awaited<ReturnType<typeof fetchPageWithComponentData>> | ReturnType<typeof fetchPageWithComponentData>;
    /** Content-tab results — Page Designer Article pages matching the query (UCR-2778). */
    contentResults?: LearnArticle[];
    /** Tab shown first: products, unless there are 0 products and ≥1 content result. */
    defaultTab?: SearchTab;
    /** Grid-section theme from the page settings (defaults to light). @see resolveSearchTheme */
    searchTheme?: DefaultSettingsTheme;
    /** Footer background theme = the theme of the last component on the page (defaults to light). */
    footerTheme?: DefaultSettingsTheme;
    pageUrl: string;
    /** Authored meta description from the Search Results page's SEO settings. (UCR-3023) */
    metaDescription?: string;
    /** Social share image: first authored region image → first product tile → Black Magic mark. */
    ogImageUrl: string;
    refine: string[];
    currency: string;
    locale: string;
    initialFiltersOpen?: boolean;
    wishlistInitialState: Promise<WishlistInitialState>;
    /** Adobe Analytics data-layer payload (UCR-2938). `resultCount` is the numeric product total. */
    analytics?: RouteAnalytics;
};

/**
 * Server-side loader function that fetches search results data.
 * This function runs on the server during SSR and prepares data for the search page.
 * @returns Object containing search results, refinements, and page metadata
 */
export async function loader(args: Route.LoaderArgs): Promise<SearchPageData> {
    const { context, request } = args;
    const requestUrl = new URL(request.url);
    const { searchParams } = requestUrl;
    const offset = parseInt(searchParams.get('offset') || '0', 10);
    const q = searchParams.get('q') ?? '';
    const sort = searchParams.get('sort') ?? '';
    const refine = searchParams.getAll('refine');
    const initialFiltersOpen = getInitialFiltersOpen(searchParams);
    const config = getConfig(context);
    const logger = getLogger(context);

    const siteCtx = context.get(siteContext);
    if (!siteCtx) {
        logger.error('Search: site context is not available');
        throw new Response('Site context is not available', { status: 500 });
    }
    const { currency } = siteCtx;
    const locale = siteCtx.locale.id;

    const limit = config.search.products.hits.limit;

    // Kick off the Page Designer page fetch in parallel with the (critical) product search. Its
    // page-level settings drive the grid-section theme, which is above the fold, so we await it
    // before returning to avoid a theme flash.
    const pagePromise = fetchPageWithComponentData(args, { pageId: 'search' });

    // Ensure criticalCount doesn't exceed limit to prevent negative non-critical limit
    const criticalCount = config.search.products.hits.critical ?? 2;
    const safeCriticalCount = Math.min(criticalCount, limit);

    logger.debug('Search: loader starting', { q, offset, sort, refineCount: refine.length });
    const searchResultCritical = await fetchSearchProducts(context, {
        q,
        limit: safeCriticalCount,
        offset,
        sort,
        refine,
        currency,
    });
    logger.info('Search: results loaded', { query: q, total: searchResultCritical.total, offset });

    // Resolved public origin, not the lambda-internal request origin, so og:url matches canonical.
    const publicOrigin = getPublicOrigin(request);
    const pageUrl = buildCanonicalUrl(publicOrigin, requestUrl.pathname, requestUrl.search);
    const effectiveCriticalCount = searchResultCritical.hits?.length ?? 0;

    const page = await pagePromise;
    const searchSettings = page?.data as { settings?: DefaultSettingsValue } | undefined;
    const searchTheme = resolveSearchTheme(searchSettings?.settings);
    const footerTheme = resolveLastComponentTheme(page, ['searchTopFullWidth', 'searchTopContent', 'searchBottom']);

    // Content tab (UCR-2778): Page Designer Article pages matching the query (Learn catalog match).
    // Cheap + cached; awaited so the default tab (products, else content when 0 products) is known
    // at first paint.
    const contentResults = await searchLearnArticles(context, q);
    const defaultTab: SearchTab =
        searchResultCritical.total === 0 && contentResults.length > 0 ? 'content' : 'products';

    return {
        searchTerm: q,
        searchResultCritical,
        searchResultNonCritical: fetchSearchProducts(context, {
            q,
            limit: limit - effectiveCriticalCount,
            offset: offset + effectiveCriticalCount,
            sort,
            refine,
            currency,
        }),
        page,
        contentResults,
        defaultTab,
        searchTheme,
        footerTheme,
        pageUrl,
        // Results pages carry no authored copy of their own — the query varies per request — so the
        // description comes from the Search Results page's SEO Description, or nothing. The old
        // query-interpolated string was hardcoded English and is gone (UCR-3023).
        metaDescription: resolvePageMetaDescription(page),
        // No thumbnail attribute on this page type: first authored region image, then the first
        // product tile in the results, then the Black Magic mark.
        ogImageUrl: resolveOgImage({
            origin: publicOrigin,
            page,
            regionIds: ['searchTopFullWidth', 'searchTopContent', 'searchBottom'],
            contentImageUrl: firstTileImageUrl(searchResultCritical.hits),
        }),
        refine,
        currency,
        locale,
        initialFiltersOpen,
        wishlistInitialState: fetchWishlistInitialState(context),
        analytics: {
            page: { pageName: buildPageName('search', 'results'), pageType: 'search-results' },
            search: { term: q, resultCount: searchResultCritical.total ?? 0 },
        },
    };
}

export function shouldRevalidate({ currentUrl, nextUrl, defaultShouldRevalidate }: ShouldRevalidateFunctionArgs) {
    // The tab (`type`) is client-only state — both product and content results are already loaded, so
    // switching tabs must not re-run the loader. Strip it alongside the other client-only params.
    const strip = (search: string): string => {
        const params = new URLSearchParams(getSearchWithoutClientOnlyParams(search));
        params.delete(TAB_PARAM);
        const serialized = params.toString();
        return serialized ? `?${serialized}` : '';
    };
    const clientOnlyParamsChanged =
        currentUrl.pathname === nextUrl.pathname &&
        currentUrl.search !== nextUrl.search &&
        strip(currentUrl.search) === strip(nextUrl.search);

    if (clientOnlyParamsChanged) {
        return false;
    }

    return defaultShouldRevalidate;
}

/** A tab in the search results tablist. */
type SearchTabDef = { id: SearchTab; label: string; count: number };

/**
 * Accessible tablist for the search results (UCR-2778). Roving tabindex + arrow/Home/End keyboard nav
 * with automatic activation (moving focus selects), mirroring the Tabs PD component. Each tab shows a
 * result count; the panels (rendered by the page) use `id="search-panel-<id>"` /
 * `aria-labelledby="search-tab-<id>"` to pair with these tabs.
 */
function SearchResultTabs({
    tabs,
    active,
    onSelect,
}: {
    tabs: SearchTabDef[];
    active: SearchTab;
    onSelect: (tab: SearchTab) => void;
}) {
    const { t } = useTranslation('search');
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const activeIndex = Math.max(
        0,
        tabs.findIndex((tabDef) => tabDef.id === active)
    );

    function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
        const { key } = event;
        let next = activeIndex;
        if (key === 'ArrowRight' || key === 'ArrowDown') next = (activeIndex + 1) % tabs.length;
        else if (key === 'ArrowLeft' || key === 'ArrowUp') next = (activeIndex - 1 + tabs.length) % tabs.length;
        else if (key === 'Home') next = 0;
        else if (key === 'End') next = tabs.length - 1;
        else return;
        event.preventDefault();
        onSelect(tabs[next].id);
        tabRefs.current[next]?.focus();
    }

    return (
        <div
            role="tablist"
            aria-label={t('resultTabsLabel', { defaultValue: 'Search result types' })}
            aria-orientation="horizontal"
            onKeyDown={onKeyDown}
            data-slot="search-result-tabs"
            className="mt-6 flex flex-wrap gap-x-8 gap-y-2 border-b border-current/15">
            {tabs.map((tabDef, index) => {
                const selected = tabDef.id === active;
                return (
                    <button
                        key={tabDef.id}
                        ref={(el) => {
                            tabRefs.current[index] = el;
                        }}
                        type="button"
                        role="tab"
                        id={`search-tab-${tabDef.id}`}
                        aria-selected={selected}
                        aria-controls={`search-panel-${tabDef.id}`}
                        tabIndex={selected ? 0 : -1}
                        onClick={() => onSelect(tabDef.id)}
                        data-slot="search-result-tab"
                        className={cn(
                            '-mb-px border-b-2 pb-3 font-sans text-lg font-bold tracking-[-0.02em] outline-none transition-colors lg:text-xl',
                            'focus-visible:ring-[3px] focus-visible:ring-ring',
                            selected ? 'border-current' : 'border-transparent opacity-60 hover:opacity-100'
                        )}>
                        {tabDef.label} <span className="font-normal tabular-nums">({tabDef.count})</span>
                    </button>
                );
            })}
        </div>
    );
}

function ProductGridError() {
    const rawError = useAsyncError();
    const error = rawError instanceof NormalizedApiError ? rawError : null;
    const { t } = useTranslation('common');
    return (
        <div role="alert" className="col-span-full py-8 text-center text-muted-foreground">
            <p>{t('productGrid.loadFailed')}</p>
            {import.meta.env.DEV && error && (
                <div className="mt-2 text-xs font-mono text-muted-foreground/70">
                    {error.status && <span>{error.status} </span>}
                    {error.message && <p>{error.message}</p>}
                </div>
            )}
        </div>
    );
}

export default function SearchPage({
    loaderData: {
        searchTerm,
        searchResultCritical,
        searchResultNonCritical,
        page,
        contentResults = [],
        defaultTab = 'products',
        searchTheme,
        pageUrl,
        metaDescription,
        ogImageUrl,
        refine,
        currency,
        locale,
        initialFiltersOpen,
        wishlistInitialState,
    },
}: {
    loaderData: SearchPageData;
}) {
    const { t } = useTranslation('search');
    const config = useConfig();
    const limit = config.search.products.hits.limit;

    // Search filtering UI (Filters button, refinements panel, active-filter chips) is off in every
    // environment (UCR-3058) — the storefront shipped without filtering, and the PLP's was removed
    // outright in UCR-2940. Optional-chained + default-hidden like <BreadcrumbSection>, so a config
    // source that hasn't populated `features` yet gets the hidden default rather than a throw. The
    // loader still parses `refine`/`filters`, so a deep-linked ?refine= keeps filtering the results.
    const showFilters = config.features?.searchFilters?.enabled ?? false;

    const [filtersOpen, toggleFiltersOpen] = useFiltersPanelState(initialFiltersOpen);

    // Tab state lives in the `type` URL param (client-only; see shouldRevalidate). Falls back to the
    // loader's computed default (products, unless 0 products and ≥1 content result).
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = resolveActiveTab(searchParams.get(TAB_PARAM), defaultTab);
    const productCount = searchResultCritical.total;
    const contentCount = contentResults.length;
    const selectTab = useCallback(
        (tab: SearchTab) => {
            setSearchParams(
                (prev) => {
                    const params = new URLSearchParams(prev);
                    params.set(TAB_PARAM, tab);
                    return params;
                },
                { replace: true, preventScrollReset: true }
            );
        },
        [setSearchParams]
    );

    // Determine the maximum number of skeletons to display in the product grid.
    // Out-of-the-box the idea is to not display more than 8 skeletons, i.e., two rows on a desktop device.
    // Wrap in Math.max(0, ...) to prevent negative values when criticalCount is high(er).
    const criticalCount = searchResultCritical.hits?.length ?? 0;
    const nonCriticalCount = Math.max(
        0,
        Math.min(8, limit, searchResultCritical.total - searchResultCritical.offset) - criticalCount
    );

    const analytics = useAnalytics();
    const lastTrackedSearchRef = useRef<string | null>(null);

    const location = useLocation();
    const navigation = useNavigation();
    const searchWithoutFiltersParam = useMemo(() => getSearchWithoutFiltersParam(location.search), [location.search]);
    const pageIdentity = `${currency}-${locale}`;
    const analyticsKey = `${pageIdentity}-${searchWithoutFiltersParam}-${location.hash}`;
    const productGridDataKey = `${pageIdentity}-${searchWithoutFiltersParam}`;
    const selectedFiltersCount = useMemo(
        () => new URLSearchParams(location.search).getAll('refine').length,
        [location.search]
    );
    const isProductGridLoading = useMemo(() => {
        if (navigation.state === 'idle' || !navigation.location) {
            return false;
        }
        const currentRefines = new URLSearchParams(location.search).getAll('refine');
        const nextRefines = new URLSearchParams(navigation.location.search).getAll('refine');
        return (
            currentRefines.length !== nextRefines.length ||
            currentRefines.some((currentRefine, index) => currentRefine !== nextRefines[index])
        );
    }, [location.search, navigation.location, navigation.state]);

    const nonCriticalPromise = useMemo(
        () => searchResultNonCritical.then((r) => r.hits ?? []),
        [searchResultNonCritical]
    );

    const [, startTransition] = useTransition();

    const handleProductClick = useCallback(
        (product: ShopperSearch.schemas['ProductSearchHit']) => {
            if (analytics) {
                void analytics.trackClickProductInSearch({
                    searchInputText: searchTerm,
                    product,
                });
            }
            // Adobe Analytics data layer (UCR-2938): search result click (e4/v4). Position is 1-based;
            // resolved from the critical (above-fold) hits where nearly all clicks land, omitted otherwise
            // (threading a full index through the deferred grid is deferred to a later phase).
            const idx = (searchResultCritical.hits ?? []).findIndex((h) => h.productId === product.productId);
            pushDataLayer('internalSearchResultClick', {
                search: { term: searchTerm, sku: product.productId },
                ...(idx >= 0 && { position: idx + 1 }),
            });
        },
        [analytics, searchTerm, searchResultCritical.hits]
    );

    useEffect(() => {
        // Only track if we haven't already tracked this search
        if (analyticsKey !== lastTrackedSearchRef.current) {
            lastTrackedSearchRef.current = analyticsKey;

            // Adobe Analytics data layer (UCR-2938): a search executed → internalSearch, then
            // internalSearchSuccess / internalSearchNull by the (numeric) product result count.
            const resultCount = searchResultCritical.total ?? 0;
            pushDataLayer('internalSearch');
            pushDataLayer(resultCount > 0 ? 'internalSearchSuccess' : 'internalSearchNull');

            startTransition(() => {
                void nonCriticalPromise
                    .then((searchHitsData: ShopperSearch.schemas['ProductSearchHit'][]) => {
                        void analytics.trackViewSearch({
                            searchInputText: searchTerm,
                            searchResults: [...(searchResultCritical.hits ?? []), ...searchHitsData],
                            sort:
                                searchResultCritical.selectedSortingOption ||
                                searchResultCritical.sortingOptions?.[0]?.label ||
                                '',
                            refinements: searchResultCritical.selectedRefinements ?? {},
                        });
                    })
                    .catch(() => {
                        // Silently handle promise rejection
                    });
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analytics, searchTerm, analyticsKey, nonCriticalPromise]);

    return (
        <WishlistProvider initialState={wishlistInitialState}>
            <SeoMeta
                title={
                    searchTerm
                        ? t('titleWithQuery', { query: searchTerm, defaultValue: `Results for "${searchTerm}"` })
                        : t('title', { defaultValue: 'Search' })
                }
                description={metaDescription}
                openGraph={{ type: 'website', url: pageUrl, image: ogImageUrl }}
            />
            <div className="-mt-8">
                {/* searchTopFullWidth — full-width region, flush to the header (mirrors PLP/home). Page
                    Designer components here span the viewport and constrain their own content width. */}
                <Region page={page} regionId="searchTopFullWidth" fallbackOnEmpty errorElement={null} />

                {/*
                 * The grid section (results header + filters + tiles) carries the merchandiser theme.
                 * The theme wrapper is full-bleed so the background color spans the viewport; a
                 * `section-container` child constrains the content to the page width. Defaults to light;
                 * both themes use the shared `bm-theme-light` / `bm-theme-dark` classes so the grid's
                 * dark background (`--bm-ink`) matches the dark theme of Page Designer components dropped
                 * in the regions (which theme via the same classes). The `searchBottom` region stays
                 * outside (below) this wrapper and full-width.
                 */}
                <div
                    className={cn(searchTheme === 'dark' ? 'bm-theme-dark' : 'bm-theme-light')}
                    data-slot="search-grid-section">
                    <div className="section-container pt-8">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <p>{t('results')}</p>
                                <h1 className="text-3xl font-bold leading-none tracking-[-0.75px]">{searchTerm}</h1>
                            </div>
                            {/* Sort applies to products only. */}
                            {activeTab === 'products' &&
                                searchResultCritical?.sortingOptions &&
                                searchResultCritical.sortingOptions.length > 0 && (
                                    <div className="flex-shrink-0">
                                        <CategorySorting result={searchResultCritical} />
                                    </div>
                                )}
                        </div>

                        {/* Tabs: toggle Products ⇄ Content, each with its result count. */}
                        <SearchResultTabs
                            tabs={[
                                {
                                    id: 'products',
                                    label: t('tabs.products', { defaultValue: 'Products' }),
                                    count: productCount,
                                },
                                {
                                    id: 'content',
                                    label: t('tabs.content', { defaultValue: 'Content' }),
                                    count: contentCount,
                                },
                            ]}
                            active={activeTab}
                            onSelect={selectTab}
                        />
                    </div>

                    {/* searchTopContent — full-width region below the header, above the results panels; its
                        components manage their own content width (not capped by section-container). */}
                    <Region className="mt-8" page={page} regionId="searchTopContent" />

                    {/* Products panel */}
                    <div
                        role="tabpanel"
                        id="search-panel-products"
                        aria-labelledby="search-tab-products"
                        hidden={activeTab !== 'products'}
                        className="section-container pb-16 pt-8">
                        {/* The product grid renders its own OOTB empty state when there are no matches. */}
                        {activeTab === 'products' && (
                            /* With filters hidden (the default — UCR-3058) the grid is the only child,
                               so the row collapses to a plain block wrapper: no flex context, no gap. */
                            <div className={cn(showFilters && 'flex flex-col lg:flex-row gap-8')}>
                                {/* Filters toggle button - mobile only (above panel) */}
                                {showFilters && (
                                    <div className="lg:hidden">
                                        <FiltersButton
                                            onClick={toggleFiltersOpen}
                                            isActive={filtersOpen}
                                            selectedFiltersCount={selectedFiltersCount}
                                        />
                                    </div>
                                )}

                                {/* Category Refinements - toggles visibility on left side */}
                                {showFilters && filtersOpen && (
                                    <div className="w-full lg:w-64 lg:flex-shrink-0">
                                        <CategoryRefinements result={searchResultCritical} refine={refine} />
                                    </div>
                                )}

                                <div className="flex-grow">
                                    {/* Filters toggle button - desktop only (inside content area) */}
                                    {showFilters && (
                                        <div className="mb-4 hidden lg:block">
                                            <FiltersButton
                                                onClick={toggleFiltersOpen}
                                                isActive={filtersOpen}
                                                selectedFiltersCount={selectedFiltersCount}
                                            />
                                        </div>
                                    )}

                                    {showFilters && <ActiveFilters result={searchResultCritical} />}

                                    <DeferredProductGrid
                                        key={productGridDataKey}
                                        critical={searchResultCritical.hits ?? []}
                                        nonCritical={nonCriticalPromise}
                                        nonCriticalCount={nonCriticalCount}
                                        hasRefinementsPanel={showFilters && filtersOpen}
                                        isLoading={isProductGridLoading}
                                        handleProductClick={handleProductClick}
                                        errorElement={<ProductGridError />}
                                    />

                                    {searchResultCritical.total > 1 && (
                                        <div className="mt-10">
                                            <CategoryPagination
                                                limit={limit}
                                                offset={searchResultCritical.offset}
                                                total={searchResultCritical.total}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Content panel — Page Designer Article results as ALP-style tiles. */}
                    <div
                        role="tabpanel"
                        id="search-panel-content"
                        aria-labelledby="search-tab-content"
                        hidden={activeTab !== 'content'}
                        className="section-container pb-16 pt-8">
                        {activeTab === 'content' &&
                            (contentResults.length > 0 ? (
                                // `auto-rows-fr` + `h-full`: equal-height tiles across the whole
                                // grid, matching the Learn listing. (UCR-3027)
                                <ul className="grid auto-rows-fr grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
                                    {contentResults.map((article) => (
                                        <li key={article.id}>
                                            <ArticleTile article={article} layout="stacked" className="h-full" />
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="py-8 text-current/70">
                                    {t('content.noResults', {
                                        query: searchTerm,
                                        defaultValue: `No articles match "${searchTerm}".`,
                                    })}
                                </p>
                            ))}
                    </div>
                </div>

                {/* searchBottom — full-width region below the grid section (outside the theme wrapper) so
                    its components manage their own background color and content width. No top margin —
                    it sits flush against the grid section. */}
                <Region page={page} regionId="searchBottom" />
            </div>
        </WishlistProvider>
    );
}
